import { adminUsersCache } from './admin/users/adminUsersCache';
import { createContext, useContext, useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { type Session, type User } from '@supabase/supabase-js';
import { supabase } from '../utils/supabase';

interface AuthContextType {
    session: Session | null;
    user: User | null;
    role: string | null;
    loading: boolean;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
    session: null,
    user: null,
    role: null,
    loading: true,
    signOut: async () => { },
});

export const useAuth = () => useContext(AuthContext);

async function fetchRole(userId: string): Promise<string | null> {
    try {
        const { data, error } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', userId)
            .single();

        if (!error && data) {
            return data.role;
        }
    } catch (error) {
        console.error('Error fetching role:', error);
    }
    return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [role, setRole] = useState<string | null>(null);
    // `authResolved` flips once the initial session (or the first auth event) is known.
    const [authResolved, setAuthResolved] = useState(false);
    // The userId whose role is currently loaded (null = resolved for a signed-out user).
    const [roleResolvedFor, setRoleResolvedFor] = useState<string | null>(null);
    // Last applied access token — lets us skip redundant session updates that would
    // otherwise churn every consumer on each background token refresh.
    const accessTokenRef = useRef<string | null>(null);

    const user = session?.user ?? null;
    const userId = user?.id ?? null;

    // Still loading until the initial session is known, and — when signed in — until
    // that user's role has been fetched. Admin guards rely on role being resolved
    // before `loading` clears, otherwise they briefly see role=null and redirect.
    const loading = !authResolved || (userId !== null && roleResolvedFor !== userId);

    // 1. Initialize the session and subscribe to auth changes.
    //    CRITICAL: the onAuthStateChange callback stays synchronous. Awaiting any
    //    supabase call inside it re-enters GoTrue's auth lock and produces a request
    //    storm (repeated /auth/v1/user + /token) that can trip refresh-token reuse
    //    detection and log everyone out. Role fetching is a separate effect (below).
    useEffect(() => {
        let mounted = true;

        let sessionOwner: string | null = null;
        const applySession = (next: Session | null) => {
            if (!next || (sessionOwner && sessionOwner !== next.user.id)) adminUsersCache.clear();
            sessionOwner = next?.user.id || null;
            const nextToken = next?.access_token ?? null;
            if (nextToken === accessTokenRef.current) return; // no real change
            accessTokenRef.current = nextToken;
            setSession(next);
        };

        supabase.auth.getSession().then(({ data: { session: initial } }) => {
            if (!mounted) return;
            applySession(initial);
            setAuthResolved(true);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, next) => {
            if (!mounted) return;
            applySession(next);          // synchronous session sync only
            setAuthResolved(true);
        });

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);

    // 2. Fetch the role when the signed-in user changes. Keyed on the primitive
    //    userId so it runs once per user, not on every token refresh.
    useEffect(() => {
        if (!userId) {
            setRole(null);
            setRoleResolvedFor(null);
            return;
        }
        let mounted = true;
        fetchRole(userId).then((nextRole) => {
            if (!mounted) return;
            if (nextRole !== 'admin') adminUsersCache.clear();
            setRole(nextRole);
            setRoleResolvedFor(userId);
        });
        return () => {
            mounted = false;
        };
    }, [userId]);

    // 3. Re-verify the session when the tab regains focus (catches a token that was
    //    refreshed in another tab). Registered once; no supabase calls run in a loop.
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;
            supabase.auth.getSession()
                .then(({ data: { session: current } }) => {
                    const nextToken = current?.access_token ?? null;
                    if (nextToken !== accessTokenRef.current) {
                        accessTokenRef.current = nextToken;
                        setSession(current);
                    }
                })
                .catch(() => { /* transient network blip, keep the local session */ });
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    const signOut = useCallback(async () => {
        await supabase.auth.signOut();
    }, []);

    const contextValue = useMemo(
        () => ({ session, user, role, loading, signOut }),
        [session, user, role, loading, signOut]
    );

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}
